import { alpha, Box, Card, Chip, darken, Stack, Theme, Typography } from "@mui/material";
import { encampBrandColors } from "colors";
import { FindingSeverity } from "generated-graphql/graphql";
import { Link as RouterLink } from "react-router-dom";

import { ComplianceFeedItem, daysUntil } from "./complianceFeed";

/**
 * Color comes from severity alone — the rules engine already weighs the domain
 * (an overdue clock is HIGH, an approaching one MEDIUM, an incomplete profile
 * LOW), so the UI does not get a second opinion.
 *
 * Deriving a band from the day count instead, as an earlier version did, meant
 * two scales that could disagree: an APPROACHING finding 8 days out read as
 * informational despite being MEDIUM, while an undated MEDIUM cap finding read
 * as a warning. The deadline still sets the chip's TEXT; only the color is
 * severity's to decide.
 */
const SEVERITY_ROLE: Record<FindingSeverity, "error" | "warning" | "info"> = {
  [FindingSeverity.High]: "error",
  [FindingSeverity.Medium]: "warning",
  [FindingSeverity.Low]: "info",
};

/**
 * Undated rows (the on-site cap findings, an incomplete profile, a missing
 * e-Manifest) name their own state instead — see `undatedLabel`. The column
 * always carries a read, since there is no dot doing that job.
 */
function urgencyLabel(item: ComplianceFeedItem, now: Date): string {
  if (!item.deadline) return item.undatedLabel;
  const days = daysUntil(item.deadline, now);
  const magnitude = Math.abs(days);
  const unit = magnitude === 1 ? "day" : "days";
  if (days < 0) return `Overdue by ${magnitude} ${unit}`;
  if (days === 0) return "Due today";
  return `${days} ${unit} remaining`;
}

/**
 * Soft-filled chip: the severity color at low alpha behind a darkened version of
 * the same hue, so the label stays legible against its own tint rather than
 * relying on MUI's solid dark fill.
 */
function severityChipSx(severity: FindingSeverity) {
  return (theme: Theme) => {
    const { main } = theme.palette[SEVERITY_ROLE[severity]];
    return {
      backgroundColor: alpha(main, 0.14),
      color: darken(main, 0.35),
      fontWeight: 500,
      border: "none",
    };
  };
}

/**
 * The source/scope tag. Container-tracking rows keep the teal brand color that
 * distinguishes them from manifest rows; manifest rows get a neutral fill.
 */
function categoryChipSx(item: ComplianceFeedItem) {
  return (theme: Theme) => {
    const base =
      item.source === "container" ? encampBrandColors.sapphire : theme.palette.text.primary;
    return {
      backgroundColor: alpha(base, 0.1),
      color: darken(base, 0.2),
      fontWeight: 500,
      border: "none",
    };
  };
}

/**
 * One row of the merged compliance feed: the condition as an overline, the
 * record it is scoped to as the title, the detail sentence, and a right-hand
 * column pairing the urgency chip with the scope it applies to.
 */
export function ComplianceFeedRow({ item, now }: { item: ComplianceFeedItem; now: Date }) {
  return (
    <Card
      variant="outlined"
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2,
        borderRadius: 2,
        // Full-strength severity color as a left rule, so the feed's urgency is
        // scannable down the edge without reading a single chip.
        borderLeft: (theme) => `4px solid ${theme.palette[SEVERITY_ROLE[item.severity]].main}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {item.kindLabel}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {item.linkTo ? (
              <Box
                component={RouterLink}
                to={item.linkTo}
                sx={{
                  color: "inherit",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {item.title}
              </Box>
            ) : (
              item.title
            )}
          </Typography>
        </Stack>
        <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
          <Chip size="small" label={urgencyLabel(item, now)} sx={severityChipSx(item.severity)} />
          <Chip size="small" label={item.categoryLabel} sx={categoryChipSx(item)} />
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {item.detail}
      </Typography>
    </Card>
  );
}
